import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Wallet as WalletIcon,
  Coins,
  ArrowRight,
  ArrowLeftRight,
  Loader2,
  X,
  History,
  RefreshCw,
  Search,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatUSD, formatDate } from "@/lib/utils";
import TransactionDetailModal from "@/components/TransactionDetailModal";
import WalletDetailModal from "@/components/WalletDetailModal";
import TransferReceiptActions from "@/components/TransferReceiptActions";
import Pagination from "@/components/Pagination";
import { formatUserLabel, pickParty, shortRef } from "@/lib/formatUser";

// Currency background tones — kept in sync with SendCash.jsx so the visual
// language is consistent across the app.
const CURRENCY_TONES = {
  USD: "bg-[#2BA8F0]",
  CAD: "bg-red-500",
  CLP: "bg-amber-500",
  DOP: "bg-blue-700",
  HTG: "bg-violet-600",
  EUR: "bg-indigo-500",
  BRL: "bg-emerald-600",
  MXN: "bg-teal-600",
};

const NO_DECIMAL = new Set(["CLP", "HTG"]);

function fmtCash(amount, currency) {
  const noDec = NO_DECIMAL.has(currency);
  const n = Number(amount || 0);
  if (noDec) return `${Math.round(n).toLocaleString("en-US")} ${currency}`;
  return `${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

// Ledger category → i18n key mapping (mirrors old Wallet.jsx).
const CATEGORY_I18N_KEY = {
  TOPUP_HOLD: "wallet.cat_topup_hold",
  TOPUP_REVERSAL: "wallet.cat_topup_reversal",
  TOPUP_TRUEUP: "wallet.cat_topup_trueup",
  COMMISSION: "wallet.cat_commission",
  MASTER_COMMISSION_PAYOUT: "wallet.cat_master_commission_payout",
  MASTER_COMMISSION_REVERSAL: "wallet.cat_master_commission_reversal",
  TRANSFER_IN: "wallet.cat_transfer_in",
  TRANSFER_OUT: "wallet.cat_transfer_out",
  FUNDING: "wallet.cat_funding",
  FUNDING_PAYKU: "wallet.cat_funding_payku",
  FUNDING_MANUAL: "wallet.cat_funding_manual",
};

// Wallet option key = "<walletType>:<currency>"
function labelForOption(o) {
  if (o.walletType === "TOPUP") return `${o.currency} · Top-up Balance`;
  if (o.currency === "USD") return `${o.currency} · Cash Wallet Balance`;
  return `${o.currency} · Cash Wallet`;
}

// ---------------------------------------------------------------------------
// Transfer Funds form + confirmation modal
// ---------------------------------------------------------------------------
function TransferFundsCard({ walletOptions, onDone }) {
  const [srcKey, setSrcKey] = useState("");
  const [dstKey, setDstKey] = useState("");
  const [srcAmt, setSrcAmt] = useState("");
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const src = useMemo(() => walletOptions.find((o) => o.key === srcKey), [walletOptions, srcKey]);
  const dst = useMemo(() => walletOptions.find((o) => o.key === dstKey), [walletOptions, dstKey]);
  const srcBalance = src ? Number(src.balance || 0) : 0;

  useEffect(() => {
    const amt = parseFloat(srcAmt);
    setPreviewErr("");
    if (!src || !dst || src.key === dst.key || !amt || amt <= 0) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setPreviewing(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.post("/wallet/convert/preview", {
          source_currency: src.currency,
          source_amount: amt,
          dest_currency: dst.currency,
          source_wallet_type: src.walletType,
          dest_wallet_type: dst.walletType,
        });
        if (!cancelled) setPreview(data);
      } catch (e) {
        if (!cancelled) {
          setPreview(null);
          setPreviewErr(e.response?.data?.detail || "Preview failed");
        }
      } finally {
        if (!cancelled) setPreviewing(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [srcKey, dstKey, srcAmt, src, dst]);

  const totalDebit = preview ? Number(preview.source_total_debit || 0) : 0;
  const insufficient = src && parseFloat(srcAmt) > 0 && (preview ? totalDebit > srcBalance : parseFloat(srcAmt) > srcBalance);
  const canSubmit =
    !!src && !!dst && src.key !== dst.key &&
    parseFloat(srcAmt) > 0 && !insufficient &&
    !!preview && !previewing;

  const openConfirm = () => {
    if (!canSubmit) return;
    setConfirming(true);
  };

  const submit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/wallet/convert", {
        source_currency: src.currency,
        source_amount: parseFloat(srcAmt),
        dest_currency: dst.currency,
        source_wallet_type: src.walletType,
        dest_wallet_type: dst.walletType,
        note: note.trim() || null,
      });
      toast.success(
        `Transferred ${fmtCash(data.source_amount, data.source_currency)} → ${fmtCash(data.dest_amount, data.dest_currency)} (fee ${fmtCash(data.fee_source_amount, data.fee_source_currency)})`
      );
      // Reset form
      setSrcKey(""); setDstKey(""); setSrcAmt(""); setNote("");
      setPreview(null); setConfirming(false);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transfer failed");
    } finally { setSubmitting(false); }
  };

  const destOptions = walletOptions.filter((o) => o.key !== srcKey);

  return (
    <div
      className="bg-white rounded-xl border border-slate-200 shadow-sm p-5"
      data-testid="transfer-funds-card"
    >
      <div className="flex items-center gap-2 mb-1">
        <ArrowLeftRight className="w-4 h-4 text-emerald-600" />
        <div className="text-sm font-semibold text-slate-900">Transfer Funds</div>
      </div>
      <div className="text-xs text-slate-500 mb-4">
        Move money between any of your wallets (Top-up ↔ Cash). Fixed fee: <strong>0.50 USD</strong> per transfer, credited to the platform.
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
            From
          </label>
          <select
            value={srcKey}
            onChange={(e) => setSrcKey(e.target.value)}
            data-testid="transfer-from-ccy"
            className="h-10 w-full rounded-md border border-slate-300 px-2 text-sm bg-white"
          >
            <option value="">— select —</option>
            {walletOptions.map((o) => (
              <option key={o.key} value={o.key}>{labelForOption(o)}</option>
            ))}
          </select>
          {src && (
            <div className="text-[11px] text-slate-500 mt-1" data-testid="transfer-from-balance">
              Balance: {fmtCash(srcBalance, src.currency)}
            </div>
          )}
        </div>
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
            Amount
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={srcAmt}
            onChange={(e) => setSrcAmt(e.target.value)}
            data-testid="transfer-amount"
            placeholder="0.00"
            className={`h-10 w-full rounded-md border px-3 text-sm font-mono text-right ${
              insufficient ? "border-rose-400 focus:ring-rose-400" : "border-slate-300 focus:ring-[#0F62FE]"
            } focus:outline-none focus:ring-2`}
          />
          {insufficient && (
            <div className="text-[11px] text-rose-600 mt-1" data-testid="transfer-insufficient">
              Exceeds available balance (includes 0.50 USD fee)
            </div>
          )}
        </div>
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
            To
          </label>
          <select
            value={dstKey}
            onChange={(e) => setDstKey(e.target.value)}
            data-testid="transfer-to-ccy"
            className="h-10 w-full rounded-md border border-slate-300 px-2 text-sm bg-white"
          >
            <option value="">— select —</option>
            {destOptions.map((o) => (
              <option key={o.key} value={o.key}>{labelForOption(o)}</option>
            ))}
          </select>
        </div>
        <div className="flex items-end">
          <button
            onClick={openConfirm}
            disabled={!canSubmit}
            data-testid="transfer-review-btn"
            className="inline-flex items-center justify-center gap-2 h-10 w-full rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ArrowLeftRight className="w-4 h-4" />
            Review transfer
          </button>
        </div>
      </div>

      <div className="mt-3">
        <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
          Note (optional)
        </label>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          data-testid="transfer-note"
          maxLength={300}
          placeholder="e.g. Convert commission USD → cash CLP · Feb 28"
          className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
        />
      </div>

      {/* Inline preview strip */}
      {(preview || previewErr || previewing) && (
        <div
          className="mt-4 rounded-md border border-slate-200 bg-slate-50/70 p-3 grid grid-cols-2 sm:grid-cols-5 gap-3"
          data-testid="transfer-preview"
        >
          {previewing && (
            <div className="col-span-full text-slate-500 text-xs flex items-center gap-1.5">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Computing conversion…
            </div>
          )}
          {previewErr && (
            <div className="col-span-full text-rose-600 text-xs">{previewErr}</div>
          )}
          {preview && !previewing && !previewErr && (
            <>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">You send</div>
                <div className="text-sm font-bold text-slate-900 mt-0.5">
                  {fmtCash(preview.source_amount, preview.source_currency)}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Fee (0.50 USD)</div>
                <div className="text-sm font-bold text-orange-700 mt-0.5" data-testid="transfer-preview-fee">
                  {fmtCash(preview.fee_source_amount, preview.fee_source_currency)}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Total debit</div>
                <div className="text-sm font-bold text-rose-700 mt-0.5" data-testid="transfer-preview-total-debit">
                  {fmtCash(preview.source_total_debit, preview.source_currency)}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">USD pivot</div>
                <div className="text-sm font-bold text-[#0F62FE] mt-0.5" data-testid="transfer-preview-usd">
                  ${Number(preview.usd_pivot_amount).toFixed(4)}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Receiver gets</div>
                <div className="text-sm font-bold text-emerald-700 mt-0.5" data-testid="transfer-preview-dest">
                  {fmtCash(preview.dest_amount, preview.dest_currency)}
                </div>
              </div>
              <div className="col-span-full text-[11px] text-slate-500 font-mono">
                1 USD = {preview.source_usd_rate} {preview.source_currency} · 1 USD = {preview.dest_usd_rate} {preview.dest_currency}
                {preview.source_currency === preview.dest_currency && <> · same-currency 1:1</>}
              </div>
            </>
          )}
        </div>
      )}

      {confirming && preview && src && dst && (
        <ConfirmTransferModal
          preview={preview}
          src={src}
          dst={dst}
          note={note}
          srcBalance={srcBalance}
          submitting={submitting}
          onCancel={() => setConfirming(false)}
          onConfirm={submit}
        />
      )}
    </div>
  );
}

function ConfirmTransferModal({ preview, src, dst, note, srcBalance, submitting, onCancel, onConfirm }) {
  const totalDebit = Number(preview.source_total_debit);
  const srcAfter = Math.max(0, srcBalance - totalDebit);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      data-testid="transfer-confirm-modal"
    >
      <div className="absolute inset-0 bg-slate-900/50" onClick={onCancel} />
      <div className="relative bg-white rounded-xl shadow-xl w-full md:max-w-lg p-6 animate-in zoom-in-95 duration-200">
        <button
          onClick={onCancel}
          className="absolute right-3 top-3 p-1.5 hover:bg-slate-100 rounded-md text-slate-500"
        >
          <X className="w-4 h-4" />
        </button>
        <div
          className="text-lg font-semibold text-slate-900 mb-1"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          Confirm internal transfer
        </div>
        <div className="text-xs text-slate-500 mb-4">
          Atomic debit of <strong>{labelForOption(src)}</strong> and credit of{" "}
          <strong>{labelForOption(dst)}</strong>. A fixed <strong>0.50 USD</strong> fee is
          collected by the platform.
        </div>

        <div className="rounded-md border border-slate-200 bg-slate-50 p-4 mb-4">
          <div className="flex items-center justify-between gap-3">
            <div className="text-center flex-1">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Debit</div>
              <div className="text-lg font-bold text-rose-700 mt-1">
                − {fmtCash(totalDebit, preview.source_currency)}
              </div>
              <div className="text-[10px] text-slate-500 mt-1 font-mono">
                {fmtCash(preview.source_amount, preview.source_currency)} + {fmtCash(preview.fee_source_amount, preview.fee_source_currency)} fee
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {fmtCash(srcBalance, preview.source_currency)} → {fmtCash(srcAfter, preview.source_currency)}
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
            <div className="text-center flex-1">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Credit</div>
              <div className="text-lg font-bold text-emerald-700 mt-1">
                + {fmtCash(preview.dest_amount, preview.dest_currency)}
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                USD pivot ${Number(preview.usd_pivot_amount).toFixed(4)}
              </div>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-slate-200 text-[11px] text-slate-600 font-mono">
            {preview.source_currency === preview.dest_currency
              ? <>Same-currency transfer 1:1 · fee 0.50 USD</>
              : <>({preview.source_amount} ÷ {preview.source_usd_rate}) × {preview.dest_usd_rate} = {preview.dest_amount}</>
            }
          </div>
        </div>

        {note && (
          <div className="text-xs text-slate-600 mb-4">
            <span className="font-semibold uppercase tracking-widest text-[10px]">Note</span>
            <div className="mt-1 text-slate-800">{note}</div>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="h-10 px-4 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50"
            data-testid="transfer-confirm-cancel"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={submitting}
            data-testid="transfer-confirm-submit"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Confirm & transfer
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Internal Transfer History table
// ---------------------------------------------------------------------------
function InternalTransferHistory({ refreshKey }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  // Free-text search over ref_id / fee_ref_id / note. `searchInput` is
  // what the user types; `search` is the debounced value that actually
  // hits the API — prevents a request storm on every keystroke.
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = async () => {
    setLoading(true);
    try {
      const skip = (page - 1) * pageSize;
      const params = new URLSearchParams({ limit: pageSize, skip });
      if (search) params.set("search", search);
      const { data } = await api.get(`/wallet/conversions?${params.toString()}`);
      setRows(data.items || []);
      setTotal(data.total || 0);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [refreshKey, page, pageSize, search]);

  const walletLabel = (ccy, wt) => wt === "TOPUP" ? `${ccy}·Top-up` : `${ccy}·Cash`;

  return (
    <div
      className="bg-white rounded-xl border border-slate-200 shadow-sm"
      data-testid="internal-transfer-history"
    >
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-slate-500" />
          <div>
            <div className="text-sm font-semibold text-slate-900">Internal Transfer History</div>
            <div className="text-xs text-slate-500 mt-0.5" data-testid="ith-total">
              {total} {total === 1 ? "conversion" : "conversions"}
              {search && (
                <span className="ml-1 text-slate-400">
                  · matching “{search}”
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search ref or note…"
              className="h-8 pl-8 pr-8 rounded-md border border-slate-300 text-xs text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 w-56"
              data-testid="ith-search-input"
            />
            {searchInput && (
              <button
                onClick={() => setSearchInput("")}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                aria-label="Clear search"
                data-testid="ith-search-clear"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-50"
            data-testid="ith-reload"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Reload
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              {[
                { k: "date", label: "Date", align: "left" },
                { k: "ref", label: "Ref", align: "left" },
                { k: "actor", label: "Actor", align: "left" },
                { k: "from_to", label: "From → To", align: "left" },
                { k: "source", label: "Source", align: "right" },
                { k: "usd_pivot", label: "USD pivot", align: "right" },
                { k: "destination", label: "Destination", align: "right" },
                { k: "fee", label: "Fee (0.50 USD)", align: "right" },
                { k: "fee_ref", label: "Fee ref", align: "left" },
                { k: "note", label: "Note", align: "left" },
                { k: "receipt", label: "Receipt", align: "left" },
              ].map((c) => (
                <th
                  key={c.k}
                  className={`bg-slate-50 ${c.align === "right" ? "text-right" : "text-left"} text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 border-b border-slate-200 whitespace-nowrap`}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={11} className="px-5 py-8 text-center text-slate-500 text-sm">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={11} className="px-5 py-8 text-center text-slate-500 text-sm">
                {search ? `No transfers matching “${search}”.` : "No internal transfers yet."}
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`ith-row-${r.ref_id}`}>
                <td className="px-4 py-3 text-slate-700 whitespace-nowrap text-xs">
                  {new Date(r.created_at).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" })}
                </td>
                <td className="px-4 py-3 font-mono text-[11px] text-slate-600" title={r.ref_id}>
                  {shortRef(r.ref_id, 10)}…
                </td>
                <td className="px-4 py-3 text-xs">
                  <div className="font-semibold text-slate-800">
                    {formatUserLabel(pickParty(r, "actor"))}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-800 whitespace-nowrap">
                    <span className={`px-1.5 py-0.5 rounded text-white text-[10px] ${CURRENCY_TONES[r.source_currency] || "bg-slate-500"}`}>
                      {walletLabel(r.source_currency, r.source_wallet_type || "CASH")}
                    </span>
                    <ArrowRight className="w-3 h-3 text-slate-400" />
                    <span className={`px-1.5 py-0.5 rounded text-white text-[10px] ${CURRENCY_TONES[r.dest_currency] || "bg-slate-500"}`}>
                      {walletLabel(r.dest_currency, r.dest_wallet_type || "CASH")}
                    </span>
                  </span>
                </td>
                <td className="px-4 py-3 font-semibold text-rose-700 whitespace-nowrap text-right">
                  − {fmtCash(r.source_amount, r.source_currency)}
                </td>
                <td className="px-4 py-3 font-semibold text-[#0F62FE] whitespace-nowrap text-right">
                  ${Number(r.usd_pivot_amount).toFixed(4)}
                </td>
                <td className="px-4 py-3 font-semibold text-emerald-700 whitespace-nowrap text-right">
                  + {fmtCash(r.dest_amount, r.dest_currency)}
                </td>
                <td className="px-4 py-3 font-semibold text-orange-700 whitespace-nowrap text-right" data-testid={`ith-fee-${r.ref_id}`}>
                  {r.fee_source_amount != null
                    ? `− ${fmtCash(r.fee_source_amount, r.fee_source_currency || r.source_currency)}`
                    : "—"}
                </td>
                <td className="px-4 py-3 font-mono text-[10px] text-slate-500 whitespace-nowrap" title={r.fee_ref_id || ""}>
                  {r.fee_ref_id ? r.fee_ref_id.slice(0, 12) + "…" : "—"}
                </td>
                <td className="px-4 py-3 text-slate-500 text-xs max-w-[220px] truncate" title={r.note || ""}>
                  {r.note || "—"}
                </td>
                <td className="px-4 py-3">
                  <TransferReceiptActions kind="internal" refId={r.ref_id} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPage={setPage}
        onPageSize={setPageSize}
        testId="internal-history-pagination"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function WalletCurrencies() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdminLike = user?.role === "MASTER_ADMIN" || user?.role === "COLLABORATOR";
  const isMasterAdmin = user?.role === "MASTER_ADMIN";

  const [walletData, setWalletData] = useState(null);
  const [cashWallet, setCashWallet] = useState(null);
  const [selected, setSelected] = useState(null);
  const [walletDetail, setWalletDetail] = useState(null); // { currency, balance }
  const [historyKey, setHistoryKey] = useState(0);

  const load = async () => {
    const [w, cw] = await Promise.all([
      api.get("/wallet"),
      api.get("/cash-wallet/me").catch(() => ({ data: null })),
    ]);
    setWalletData(w.data);
    setCashWallet(cw.data);
  };

  useEffect(() => { load(); }, []);

  const topupUsd = useMemo(() => {
    if (!walletData) return null;
    if (isAdminLike) return walletData.internal_allocation_usd ?? 0;
    return walletData.balance_usd ?? 0;
  }, [walletData, isAdminLike]);

  const sendCashCards = useMemo(() => {
    if (!cashWallet) return [];
    if (Array.isArray(cashWallet.balances) && cashWallet.balances.length > 0) {
      return cashWallet.balances;
    }
    return [{ currency: cashWallet.currency || "USD", balance: cashWallet.balance ?? 0 }];
  }, [cashWallet]);

  // Consolidated dropdown list for the Transfer Funds form:
  // Top-up USD (from wallets.balance_usd) + one entry per Send Cash currency.
  const walletOptions = useMemo(() => {
    const options = [];
    if (topupUsd != null) {
      options.push({
        key: "TOPUP:USD",
        walletType: "TOPUP",
        currency: "USD",
        balance: topupUsd,
      });
    }
    for (const b of sendCashCards) {
      options.push({
        key: `CASH:${b.currency}`,
        walletType: "CASH",
        currency: b.currency,
        balance: b.balance,
      });
    }
    return options;
  }, [topupUsd, sendCashCards]);

  const onTransferDone = async () => {
    await load();
    setHistoryKey((k) => k + 1);
  };

  // Top-up card styling by role
  const topupIsInternalPool = isMasterAdmin;
  const topupCardClass = topupIsInternalPool
    ? "bg-gradient-to-br from-emerald-600 to-emerald-800"
    : "bg-gradient-to-br from-[#0F62FE] to-[#0043A8]";
  const topupSubLabel = topupIsInternalPool
    ? t("wallet_currencies.internal_pool", "Internal pool")
    : t("wallet_currencies.topup_wallet_label", "Top-up Wallet Balance");
  const topupTitle = topupIsInternalPool
    ? t("wallet_currencies.master_wallet_balance_topup", "Master Wallet Balance Topup")
    : t("wallet_currencies.topup_wallet_balance", "Top-up Wallet Balance");

  return (
    <div className="space-y-6" data-testid="wallet-currencies-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
          {t("nav.folder_my_wallet", "My Wallet")}
        </div>
        <h1
          className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          {t("nav.folder_currencies", "Currencies")}
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          {t("wallet_currencies.subtitle", "Your Master Wallet in USD and every Send Cash currency you're authorised to hold.")}
        </p>
      </div>

      <div
        className={`${topupCardClass} rounded-xl shadow-sm p-6 text-white max-w-md`}
        data-testid={topupIsInternalPool ? "master-wallet-card" : "topup-wallet-card"}
      >
        <div className="flex items-center gap-2 text-white/85 text-[11px] uppercase tracking-widest font-semibold">
          <WalletIcon className="w-4 h-4" />
          {topupTitle}
        </div>
        <div
          data-testid={topupIsInternalPool ? "master-wallet-usd" : "topup-wallet-usd"}
          className="text-4xl font-bold mt-2"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          {topupUsd == null ? "—" : formatUSD(topupUsd)}
        </div>
        <div className="text-white/80 text-xs mt-2">
          USD · {topupSubLabel}
        </div>
      </div>

      <section data-testid="send-cash-wallets-section">
        <div className="flex items-center gap-2 mb-2">
          <Coins className="w-3.5 h-3.5 text-slate-500" />
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
            {t("wallet_currencies.send_cash_wallets", "Cash Wallets")}
          </div>
        </div>
        {sendCashCards.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 p-6 text-sm text-slate-500 bg-white">
            {t("wallet_currencies.no_cash_wallets", "No Send Cash wallets available.")}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {sendCashCards.map((b) => (
              <button
                type="button"
                key={b.currency}
                data-testid={`wallet-currency-${b.currency}`}
                onClick={() => setWalletDetail({ currency: b.currency, balance: b.balance })}
                className={`${CURRENCY_TONES[b.currency] || "bg-slate-500"} text-white rounded-md p-4 text-left transition-transform hover:scale-[1.02] hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-white/60 cursor-pointer`}
              >
                <div className="text-[10px] uppercase tracking-widest opacity-90 font-semibold">
                  {b.currency} {b.currency === "USD" ? "· Cash Wallet" : "· Cash"}
                </div>
                <div
                  className="text-2xl font-bold mt-1 leading-tight break-words"
                  style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                >
                  {fmtCash(b.balance, b.currency).replace(` ${b.currency}`, "")}
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Transfer Funds — available whenever the user has ≥ 2 wallets */}
      {walletOptions.length >= 2 && (
        <TransferFundsCard walletOptions={walletOptions} onDone={onTransferDone} />
      )}

      {/* Internal transfer history */}
      <InternalTransferHistory refreshKey={historyKey} />

      <TransactionDetailModal entry={selected} onClose={() => setSelected(null)} />
      {walletDetail && (
        <WalletDetailModal
          currency={walletDetail.currency}
          balance={walletDetail.balance}
          onClose={() => setWalletDetail(null)}
        />
      )}
    </div>
  );
}
